import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, Wallet, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, getNetwork, setNetwork } from "@/lib/api";
import { clearSession, connectWallet, getSession, pullAndMergeWatchlist } from "@/lib/wallet";
import { shortHash } from "@/lib/format";
import SearchBar from "@/components/SearchBar";

const GM_TOKEN_ADDRESS = "0x470eeb8ff0a7b5c9c830f618cb5522014db78888";

export function GmTokenPill({ full = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(GM_TOKEN_ADDRESS);
      setCopied(true);
      toast.success("$GM contract address copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy address");
    }
  };
  return (
    <button
      data-testid="gm-token-copy"
      onClick={copy}
      title={`$GM: ${GM_TOKEN_ADDRESS} — click to copy`}
      className={`group flex items-center gap-1.5 text-xs font-mono whitespace-nowrap border border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 text-primary rounded-lg px-2.5 py-1.5 transition-colors duration-200 ${full ? "w-full justify-center" : ""}`}
    >
      <span className="font-sans font-semibold">$GM</span>
      <span className="text-primary/70">·</span>
      <span className="tracking-tight">{shortHash(GM_TOKEN_ADDRESS, 4)}</span>
      {copied ? <Check size={12} /> : <Copy size={12} className="opacity-60 group-hover:opacity-100" />}
    </button>
  );
}

const NAV = [
  { to: "/", label: "Home" },
  { to: "/blocks", label: "Blockchain" },
  { to: "/txs", label: "Transactions" },
  { to: "/tokens", label: "Tokens" },
  { to: "/stocks", label: "Stock Tokens" },
  { to: "/analytics", label: "Analytics" },
  { to: "/watchlist", label: "Watchlist" },
];

export function WalletButton({ full = false }) {
  const [session, setSession] = useState(getSession());
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      await connectWallet();
      const merged = await pullAndMergeWatchlist();
      setSession(getSession());
      toast.success(`Wallet connected — watchlist synced (${merged?.length ?? 0} items)`);
      window.dispatchEvent(new Event("gmscan:wallet-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not connect wallet");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = () => {
    clearSession();
    setSession(null);
    toast("Wallet disconnected — your watchlist stays in this browser");
    window.dispatchEvent(new Event("gmscan:wallet-changed"));
  };

  return session ? (
    <button data-testid="wallet-disconnect-btn" onClick={disconnect} title="Click to disconnect"
      className={`flex items-center gap-1.5 text-xs border border-primary/40 bg-primary/10 text-primary rounded-lg px-2.5 py-1.5 transition-colors duration-200 hover:bg-primary/20 ${full ? "w-full justify-center" : ""}`}>
      <Wallet size={12} /> {shortHash(session.address, 4)}
    </button>
  ) : (
    <button data-testid="wallet-connect-btn" onClick={connect} disabled={busy}
      className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 text-muted-foreground hover:text-foreground transition-colors duration-200 ${full ? "w-full justify-center" : ""}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Wallet size={12} />} Connect
    </button>
  );
}

export default function Header({ theme, setTheme }) {
  const [online, setOnline] = useState(null);
  const [open, setOpen] = useState(false);
  const [network, setNet] = useState(getNetwork());
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    let alive = true;
    const check = () =>
      api.get("/stats").then(() => alive && setOnline(true)).catch(() => alive && setOnline(false));
    check();
    const t = setInterval(check, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [network]);

  useEffect(() => setOpen(false), [location]);

  const switchNetwork = (n) => {
    setNetwork(n);
    setNet(n);
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-40 border-b backdrop-blur-xl bg-background/70">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 h-16 flex items-center gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
          <img src="/logo64.png" alt="GMScan logo" className="h-8 w-8 rounded-lg" />
          <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>GMScan</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors duration-200 ${
                  isActive ? "text-primary bg-primary/10 font-semibold" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        {!isHome && (
          <div className="hidden md:block w-72 shrink-0">
            <SearchBar compact />
          </div>
        )}

        <div data-testid="network-status" className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground border rounded-full px-2.5 py-1">
          <span className={`h-2 w-2 rounded-full pulse-dot ${online === false ? "bg-destructive" : online ? "bg-primary" : "bg-muted-foreground"}`} />
          {online === false ? "Offline" : online ? "Live" : "…"}
        </div>

        <select
          data-testid="network-switcher"
          value={network}
          onChange={(e) => switchNetwork(e.target.value)}
          className="hidden sm:block bg-secondary border rounded-lg text-xs px-2 py-1.5 outline-none cursor-pointer"
        >
          <option value="mainnet">Mainnet</option>
          <option value="testnet">Testnet</option>
        </select>

        <div className="hidden md:block"><GmTokenPill /></div>

        <div className="hidden sm:block"><WalletButton /></div>

        <button
          data-testid="theme-toggle"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="h-8 w-8 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </button>

        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden h-8 w-8 rounded-lg border flex items-center justify-center">
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t bg-background/95 backdrop-blur-xl px-4 py-3 space-y-1">
          <div className="mb-3"><SearchBar compact /></div>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `block px-3 py-2 rounded-lg text-sm ${isActive ? "text-primary bg-primary/10" : "text-muted-foreground"}`}>
              {n.label}
            </NavLink>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <select value={network} onChange={(e) => switchNetwork(e.target.value)} className="bg-secondary border rounded-lg text-xs px-2 py-1.5 w-full">
              <option value="mainnet">Mainnet</option>
              <option value="testnet">Testnet</option>
            </select>
            <WalletButton full />
          </div>
          <div className="pt-2"><GmTokenPill full /></div>
        </div>
      )}
    </header>
  );
}
